/**
 * Product settings slot for FusionMSI global chrome.
 *
 * WHAT: Lets the active product page register one "Open … settings" entry that
 *       AppSettingsMenu can show when unified chrome is enabled.
 * WHY: Avoid dumping every product's settings UI into AppSettingsMenu while still
 *      consolidating gears under FusionMSI chrome.
 * Coupling: AppPageShell provider, AppSettingsMenu, VoxPont4Page.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

const ProductSettingsContext = createContext({
  registration: null,
  registerProductSettings: () => {},
  unregisterProductSettings: () => {},
});

/**
 * @param {{ children: React.ReactNode }} props
 */
export function ProductSettingsProvider({ children }) {
  const [registration, setRegistration] = useState(null);

  const registerProductSettings = useCallback((next) => {
    if (!next?.id || typeof next.open !== 'function') return;
    setRegistration({
      id: next.id,
      label: next.label || 'Product settings',
      open: next.open,
    });
  }, []);

  const unregisterProductSettings = useCallback((id) => {
    setRegistration((prev) => (prev?.id === id ? null : prev));
  }, []);

  const value = useMemo(
    () => ({
      registration,
      registerProductSettings,
      unregisterProductSettings,
    }),
    [registration, registerProductSettings, unregisterProductSettings]
  );

  return (
    <ProductSettingsContext.Provider value={value}>{children}</ProductSettingsContext.Provider>
  );
}

ProductSettingsProvider.propTypes = {
  children: PropTypes.node,
};

export function useProductSettingsSlot() {
  return useContext(ProductSettingsContext);
}

/**
 * Register a product settings opener for the lifetime of the calling component.
 * @param {{ id: string, label: string, open: () => void } | null} registration
 */
export function useRegisterProductSettings(registration) {
  const { registerProductSettings, unregisterProductSettings } = useProductSettingsSlot();
  const id = registration?.id;
  const label = registration?.label;
  const open = registration?.open;

  useEffect(() => {
    if (!id || typeof open !== 'function') return undefined;
    registerProductSettings({ id, label, open });
    return () => unregisterProductSettings(id);
  }, [id, label, open, registerProductSettings, unregisterProductSettings]);
}

export default ProductSettingsContext;
